import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { applyAISummaryLimit } from 'src/reactQuery/utils/aiSummaryCache'
import { ContentAnalysisApiService } from 'src/services/contentAnalysisApi'
import { ContentAnalysisErrorResponse } from 'src/services/contentAnalysisApi/interfaces/contentAnalysis.interface'
import {
  ContentAnalysisSummaryGenerateResponse,
  GenerateContentAnalysisSummaryPayload,
} from 'src/services/contentAnalysisApi/interfaces/contentAnalysisSummary.interface'

interface UseGenerateContentAnalysisSummaryMutationParams {
  id: string
  onError?: (error: AxiosError<ContentAnalysisErrorResponse>) => void
  onSuccess?: (data: ContentAnalysisSummaryGenerateResponse) => void
}

export const useGenerateContentAnalysisSummaryMutation = ({
  id,
  onError,
  onSuccess,
}: UseGenerateContentAnalysisSummaryMutationParams) => {
  const queryClient = useQueryClient()
  const scopeKey = [QueryKey.GET_CONTENT_ANALYSIS_SUMMARY, id]
  const refresh = () => queryClient.invalidateQueries({ queryKey: scopeKey })

  return useMutation({
    mutationFn: (payload: GenerateContentAnalysisSummaryPayload) =>
      ContentAnalysisApiService.generateSummary<ContentAnalysisSummaryGenerateResponse>(
        id,
        payload
      ),
    onError: (error: AxiosError<ContentAnalysisErrorResponse>) => {
      toast.error(error.response?.data?.error || 'Failed to generate summary')
      // 409 (period summarised < 24h ago) / 429 (daily cap): refetch so the
      // summary or countdown shows instead of a button that keeps failing.
      const status = error.response?.status
      if (status === 409 || status === 429) {
        applyAISummaryLimit(queryClient, scopeKey, {
          nextAvailableAt: error.response?.data?.nextAvailableAt,
        })
        refresh()
      }
      onError?.(error)
    },
    onSuccess: (data) => {
      applyAISummaryLimit(queryClient, scopeKey, {
        nextAvailableAt: data.data?.nextAvailableAt,
        summary: data.data?.summary,
      })
      refresh()
      onSuccess?.(data)
    },
  })
}
